<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
    <!-- 页面头部 -->
    <div class="flex items-center gap-4 mb-6">
      <button
        @click="handleBack"
        class="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
      >
        <ArrowLeftIcon class="w-6 h-6" />
      </button>
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">编辑公告</h1>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 表单 -->
    <AnnouncementForm v-else-if="announcement" mode="edit" :initial-data="announcement" @submit="handleSubmit"
      @cancel="handleBack" />

    <!-- 错误状态 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
      <p class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">加载失败</p>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">无法加载公告数据</p>
      <button @click="handleBack"
        class="primary-btn">
        返回列表
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ArrowLeftIcon } from '@heroicons/vue/24/outline'
import AnnouncementForm from '@/components/announcement/AnnouncementForm.vue'
import { getAnnouncementAPI, updateAnnouncementAPI } from '@/api/announcement'
import toaster from '@/utils/toaster'
import type { Announcement, UpdateAnnouncementRequest } from '@/types/announcement'

/**
 * 路由实例
 */
const router = useRouter()
const route = useRoute()

/**
 * 状态
 */
const loading = ref(true)
const announcement = ref<Announcement | null>(null)

/**
 * 加载公告数据
 */
const loadAnnouncement = async () => {
  const announcementId = Number(route.params.id)
  if (!announcementId) {
    toaster.error('无效的公告 ID')
    router.push('/announcement')
    return
  }

  loading.value = true
  try {
    const response = await getAnnouncementAPI(announcementId)
    if (response.data) {
      announcement.value = response.data
    } else {
      toaster.error('加载公告失败')
      router.push('/announcement')
    }
  } catch (error) {
    console.error('加载公告失败:', error)
    toaster.error('加载公告失败')
    router.push('/announcement')
  } finally {
    loading.value = false
  }
}

/**
 * 返回列表页
 */
const handleBack = () => {
  router.push('/announcement')
}

/**
 * 处理表单提交
 */
const handleSubmit = async (data: UpdateAnnouncementRequest) => {
  const announcementId = Number(route.params.id)
  try {
    const response = await updateAnnouncementAPI(announcementId, data)

    if (response.status === 200 || response.status === 'success') {
      toaster.success('公告更新成功')
      router.push('/announcement')
    } else {
      toaster.error(response.message || '公告更新失败')
    }
  } catch (error: any) {
    console.error('更新公告失败:', error)
    toaster.error(error.message || '公告更新失败,请稍后重试')
  }
}

// 组件挂载时加载数据
onMounted(() => {
  loadAnnouncement()
})
</script>

